import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import { usePermissions } from '#composables/usePermissions';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { listActivityTypes } from '#domain/core/filter/filter.query';
import {
  extractFilterKeyFromNodeId,
  isEcommerceActivityType,
  isInStoreActivityType,
  type ProductTaxonomyType
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

interface UseActivityTypeDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useActivityTypeData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy
}: UseActivityTypeDataProps) {
  const { t } = useLanguage();

  const { hasAccessToEcommerce } = usePermissions();

  const {
    data: activityTypeData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [listActivityTypes.name, debouncedSearch, taxonomy],
    queryFn: async (ctx) => listActivityTypes({ taxonomy: unref(taxonomy) }, ctx),
    select: (value) => (value.status === 'success' ? value.data : undefined)
  });

  const itemsActivityTypes = computed(() => {
    if (!activityTypeData.value) {
      return undefined;
    }

    const activityTypesFiltered = activityTypeData.value?.children?.filter(
      (i) =>
        i.label.includes(debouncedSearch.value) ||
        checkedItemsState.value.activityTypesFilters[i.nodeId]?.state === 'checked'
    );

    if (!activityTypesFiltered) {
      return undefined;
    }

    const disabledActivityTypeInformation = (item: (typeof activityTypesFiltered)[number]) => {
      if (isEcommerceActivityType(item.nodeId) && !hasAccessToEcommerce.value) {
        return {
          disabled: true,
          disabledMessage: t('performanceCarrefourCatman.storePerimeter.hintEcommerceNotAvailable')
        };
      }

      if (isInStoreActivityType(item.nodeId) && !hasAccessToEcommerce.value) {
        return {
          disabled: true
        };
      }

      return undefined;
    };

    const activityTypesData = {
      ...activityTypeData.value,
      children: activityTypesFiltered?.map((item) => {
        const extractedFilterKey = extractFilterKeyFromNodeId(item.nodeId);

        return {
          ...item,
          label: t(`filterPopin.activityTypesData.${extractedFilterKey}`),
          ...disabledActivityTypeInformation(item)
        };
      })
    };

    return filterToTreeViewItemWithDefaultValue({
      filter: activityTypesData,
      defaultFilters: activityTypesData,
      selectedFilters: checkedItemsState.value.activityTypesFilters
    });
  });

  const isLoadingActivityTypes = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsActivityTypes, isLoadingActivityTypes };
}
